import { useContext, useEffect, useState } from "react";
import { MdArrowOutward } from "react-icons/md";
import { AppContext } from "../App";
import { getInlineLoader } from "../Helpers";
import OneService from "./OneService";

function stripHtml(value = "") {
  return String(value).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function extractHighlights(body = "") {
  const matches = String(body).match(/<strong[^>]*>.*?<\/strong>/gi) || [];
  const uniqueItems = [];

  matches.forEach((item) => {
    const cleanItem = stripHtml(item);

    if (cleanItem && !uniqueItems.includes(cleanItem)) {
      uniqueItems.push(cleanItem);
    }
  });

  return uniqueItems;
}

function groupServices(services = []) {
  const grouped = {
    zanzibar: [],
    mainland: [],
  };

  services.forEach((service) => {
    const categoryTitle = String(service.category_title || service.categories || "").toLowerCase();

    if (categoryTitle.includes("mainland")) {
      grouped.mainland.push(service);
    } else if (categoryTitle.includes("zanzibar")) {
      grouped.zanzibar.push(service);
    }
  });

  return grouped;
}

function ServiceRegionSection({ title, subtitle, services, isHome, appContext }) {
  return (
    <div className="service-region-block">
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-end mb-4">
        <div>
          <div className="tff-section-kicker">{title}</div>
          <h3 className="service-region-title">{subtitle}</h3>
        </div>

        {isHome ? (
          <button
            className="btn btn-outline-primary btn-rounded btn-padded mt-3 mt-lg-0"
            onClick={() => {
              appContext.navTo({ item: "services" });
            }}
          >
            View All Services
            <MdArrowOutward size={18} className="ms-2" />
          </button>
        ) : (
          ""
        )}
      </div>

      <div className="row">
        {services.map((service) => (
          <div key={service.uuid} className="col-lg-4 mb-4">
            <OneService
              uuid={service.uuid}
              title={service.title}
              image={service.photo}
              description={service.description}
              categoryTitle={service.category_title}
              items={extractHighlights(service.body)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ServicesBanner1({ isHome = false }) {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);

  async function init() {
    setReady(false);

    if (!appContext.allServices) {
      await appContext.getAllServices?.();
    }

    setReady(true);
  }

  useEffect(() => {
    init();
  }, []);

  if (!ready) {
    return (
      <div className="container mSupportLoading">
        {getInlineLoader()}
      </div>
    );
  }

  const groupedServices = groupServices(appContext.allServices || []);
  const zanzibarServices = isHome ? groupedServices.zanzibar.slice(0, 3) : groupedServices.zanzibar;
  const mainlandServices = isHome ? groupedServices.mainland.slice(0, 3) : groupedServices.mainland;
  const hasServices = zanzibarServices.length > 0 || mainlandServices.length > 0;

  return (
    <div className="MainBodySection bg-background" style={{ paddingTop: "60px", paddingBottom: "70px" }}>
      <div className="container">
        <div className="row align-items-end mb-4">
          <div className="col-lg-7">
            <div className="tff-section-kicker">What We Do Best</div>
            <h2 className="tff-section-title">
              Services designed for smooth, memorable Tanzania travel.
              <span className="tff-section-underline"></span>
            </h2>
          </div>
          <div className="col-lg-5">
            <p className="font-light tff-section-lead">
              We operate across Zanzibar Island and the mainland, combining accommodation planning, transport logistics,
              guided excursions, and safari expertise in one coordinated service flow.
            </p>
          </div>
        </div>

        {hasServices ? (
          <>
            {zanzibarServices.length > 0 ? (
              <ServiceRegionSection
                title="Zanzibar Services"
                subtitle="Island stays, transfers, and excursion support"
                services={zanzibarServices}
                isHome={isHome}
                appContext={appContext}
              />
            ) : (
              ""
            )}

            {mainlandServices.length > 0 ? (
              <ServiceRegionSection
                title="Mainland Services"
                subtitle="Safaris, circuits, and mainland travel coordination"
                services={mainlandServices}
                isHome={isHome}
                appContext={appContext}
              />
            ) : (
              ""
            )}
          </>
        ) : (
          <div className="card service-list-card">
            <div className="card-body p-4 p-lg-5 text-center">
              <div className="tff-section-kicker">Services</div>
              <h3 className="font-light text-primary mt-3">Fresh travel experiences are on the way</h3>
              <p className="font-light tff-section-lead mt-3 mb-0">
                Published services will appear here once they are organized under Zanzibar and Mainland categories.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
